<template>
    <div class="boq-unassigned-page">
    <div v-if="!isDeluxeLayout">
        <BreadcrumbsProject
        :breadcrumbs="[
            {
                link : '/high-level-estimates',
                title : $t('Bidding'),
                isRouterLink: true
            },
            {
                link : '/high-level-estimates/' + hleProjectId + '/bill-of-quantities',
                title : (hleProject.projectName || '-' )+ ' - ' + $t('Bidding Module.Detailed Estimates'),
                isRouterLink: true
            },
            {
                link : '/high-level-estimates/' + hleProjectId + '/bill-of-quantities-builder/' + boqId,
                title :boqUrlname,
                isRouterLink: true
            },
            {
                link : '/high-level-estimates/' + hleProjectId + '/bill-of-quantities-builder/' + boqId + '/Unassigneds',
                title :$t('Unassigned Line Items'),
                isRouterLink: true
            },
        ]"
        />
   </div>
    <div class="deluxe-project-back boq-unassigned-back">
        <router-link :to="{ name: 'HleBoQBuilder', params: { id: hleProjectId, boqId } }">
            <i class="fa-solid fa-chevron-left"></i> {{ $t('Back to Bill of Quantity') }}
        </router-link>
    </div>
   <div class="card boq-unassigned-card">
        <div class="card-header bg-white boq-unassigned-card__header">
            <div class="boq-unassigned-heading">
                <div>
                    <span class="boq-unassigned-eyebrow">{{ $t('Bidding') }}</span>
                    <h1>{{ boqItem.name || '-' }} - {{ $t('Unassigned Line Items') }}</h1>
                    <p>{{ $t('Select line items, then attach them to a section or remove them.') }}</p>
                </div>
                <div class="boq-unassigned-summary" aria-label="Line item summary">
                    <div class="boq-unassigned-summary__metrics">
                        <div class="boq-unassigned-summary__item">
                            <span>{{ $t('Line Items') }}</span>
                            <strong>{{ lineItems.length }}</strong>
                        </div>
                        <div class="boq-unassigned-summary__divider"></div>
                        <div class="boq-unassigned-summary__item boq-unassigned-summary__item--selected">
                            <span>{{ $t('Selected') }}</span>
                            <strong>{{ selectedLineItems.length }}</strong>
                        </div>
                    </div>
                    <div class="boq-unassigned-progress">
                        <div class="boq-unassigned-progress__label">
                            <span>{{ $t('Selection Progress') }}</span>
                            <strong>{{ selectedLineItems.length }} / {{ lineItems.length }} · {{ selectionProgress }}%</strong>
                        </div>
                        <div
                        class="boq-unassigned-progress__track"
                        role="progressbar"
                        :aria-label="$t('Selection Progress')"
                        :aria-valuenow="selectionProgress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        >
                            <span :style="{ width: selectionProgress + '%' }"></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-body boq-unassigned-card__body">
            <div class="boq-unassigned-toolbar">
                <div class="boq-unassigned-tools">
                    <button class="btn boq-unassigned-tool"
                    :class="{ 'boq-unassigned-tool--active': OpenFilter }"
                    @click="()=>{ OpenFilter = !OpenFilter}" 
                    :aria-expanded="OpenFilter"
                    >
                        <i class="fa-solid fa-filter"></i> {{$t('Filter')}}
                        <span v-if="activeFilterCount" class="boq-unassigned-tool__count">{{ activeFilterCount }}</span>
                    </button>
                    <button class="btn boq-unassigned-tool"
                    data-bs-toggle="modal" data-bs-target="#BoqColumnFilter"
                    >
                        <i class="fa-solid fa-eye"></i>  {{$t('Columns')}}
                    </button>
                </div>
                <div class="boq-unassigned-actions" v-if="!IsQuotationPublished">
                    <span v-if="selectedLineItems.length" class="boq-unassigned-selection-note">
                        <i class="fa-solid fa-circle-check"></i> {{ selectedLineItems.length }} {{ $t('Selected') }}
                    </span>
                    <button class="btn boq-unassigned-action boq-unassigned-action--primary"
                    data-bs-toggle="modal" 
                    data-bs-target="#BoqAttachModal" 
                    :disabled="selectedLineItems.length === 0"
                    > <i class="fa-solid fa-link"></i> {{ $t('Attach to Section') }}</button>
                    <button class="btn boq-unassigned-action boq-unassigned-action--danger"
                    data-bs-toggle="modal" 
                    data-bs-target="#BoqDeleteLineItemsModal" 
                    :disabled="selectedLineItems.length === 0"
                    > <i class="fa-solid fa-trash-can"></i> {{$t('Delete Line Items')}}</button>
                </div>
            </div>
            <transition name="boq-filter">
            <div class="boq-unassigned-filters" v-if="OpenFilter">
                <div class="boq-unassigned-filter">
                    <div class="form-group ">
                        <label>
                            <span><i class="fa-solid fa-tags"></i> {{$t('Cost Category')}}</span>
                            <button type="button" @click="ClearField(2)" :disabled="selectedCostCode.length === 0">{{ $t('Clear Field') }}</button>
                        </label>
                        <Multiselect 
                        :multiple="true" :options="searchableCostCodes" 
                        :placeholder="$t('Search Here')"
                        v-model="selectedCostCode" :max-height="300"
                        label="costName" track-by="_id"/>
                    </div>
                </div>
                <div class="boq-unassigned-filter">
                    <div class="form-group ">
                        <label>
                            <span><i class="fa-solid fa-location-dot"></i> {{$t('Space ID')}}</span>
                            <button type="button" @click="ClearField(1)" :disabled="selectedArea.length === 0">{{ $t('Clear Field') }}</button>
                        </label>
                        <Multiselect 
                        :multiple="true" :options="searchableAreas" 
                        :placeholder="$t('Search Here')"
                        v-model="selectedArea"  :max-height="300" />
                    </div>
                </div>
            </div>
            </transition>
            
            <div class="boq-unassigned-currency-note">
                <i class="fa-solid fa-circle-info"></i>
                <FigureCurrencyNote :currency="boqItem.currencyId" />
            </div>
            <Loader  v-if="!dataReady"/>
            <div class="table-responsive boq-unassigned-table-wrap">
            <table class="table table-header-solid boq-unassigned-table" >
                <thead class="sticky-top">
                    <tr >
                        <th class="boq-unassigned-table__check sticky-left text-center" valign="middle" v-if="!IsQuotationPublished">
                            <input type="checkbox" @change="OnChecked" :checked="allItemsSelected" :aria-label="$t('Select all line items')" />
                        </th>
                        <th class="boq-unassigned-table__number">{{$t('S/N')}}</th>
                        <th class="boq-unassigned-table__description">{{$t('Description')}}</th>
                        <th class="boq-unassigned-table__image">{{$t('Image')}}</th>
                        <th class="boq-unassigned-table__space">{{$t('Space ID')}}</th>
                        <th>{{$t('Unit')}}</th>
                        <BoqLineItemRowThead 
                        :selectedColumns="selectedColumns" />
                        <th class="sticky-right boq-unassigned-table__actions">{{$t('Actions')}}</th>
                    </tr>
                </thead>
                <tbody class="table-group-divider"  v-if="lineItems.length==0">
                    <tr>
                        <td class="boq-unassigned-empty" colspan="25">
                            <i class="fa-solid fa-circle-check"></i>
                            <strong>{{ $t('No data Found') }}</strong>
                            <span>{{ $t('Unassigned Line Items') }}</span>
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else >
                        <template v-for="(element,key) in LineItemData" :key="key">
                            <tr :class="{ 'boq-unassigned-table__row--selected': selectedLineItems.includes(element._id) }">
                                <td class="text-center" valign="middle" v-if="!IsQuotationPublished">
                                    <input type="checkbox" v-model="selectedLineItems" :value="element._id" :aria-label="`${$t('Select')} ${element.name}`" />
                                </td>
                                <td class="text-center">
                                    <span class="boq-unassigned-row-number">{{element.label}}</span>
                                </td>
                                <td class="boq-unassigned-description-cell">
                                    <div class="boq-unassigned-description">
                                        <div  class="d-flex justify-content-between"  >
                                            <span class="fw-bold text-capitalize" v-if="userInfo.language=='en'">{{element.name}}</span>
                                            <span class="fw-bold" v-else>{{element.nameCh || element.name}}</span>
                                            <span>
                                                <small v-if="element.remark" class="text-info me-1 bg-light px-1 rounded">{{ $t('BillOfQuantityRemark.' + element.remark) }}</small>
                                                <small v-if="element.rateOnly" class="text-danger bg-light px-1 rounded">{{$t('RATE ONLY')}}</small>
                                            </span>
                                        </div>
                                        <p class="small mb-0 description-line-item">
                                            {{element.description}}
                                        </p>
                                    </div>
                                </td>
                                <td class="boq-unassigned-image-cell">
                                    <button type="button" class="boq-unassigned-image-button" :class="{ 'boq-unassigned-image-button--empty': !imageSummaries[element._id]?.image }" :title="$t('Images') + ': ' + element.name" @click="OpenImages(element, $event)">
                                        <template v-if="imageSummaries[element._id]?.image">
                                            <span class="boq-unassigned-image-frame">
                                                <BoqItemImage
                                                class="boq-unassigned-image-preview"
                                                :class="{ 'boq-unassigned-image-preview--loading': !loadedImagePreviews[element._id] }"
                                                :boqId="boqId"
                                                :itemId="element._id"
                                                :image="imageSummaries[element._id].image"
                                                imageContextType="high-level"
                                                :alt="imageSummaries[element._id].image.originalFilename || element.name"
                                                @load="loadedImagePreviews[element._id] = true"
                                                @error="imageSummaryStates[element._id] = 'error'" />
                                            </span>
                                            <small>{{ imageSummaries[element._id].count }} {{ $t('images') }}</small>
                                        </template>
                                        <template v-else-if="imageSummaryStates[element._id] === 'loading'">
                                            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                            <small>{{ $t('Loading images') }}</small>
                                        </template>
                                        <template v-else>
                                            <i class="fa-regular fa-images" aria-hidden="true"></i>
                                            <small>{{ $t('Add image') }}</small>
                                        </template>
                                    </button>
                                </td>
                                <td><span class="boq-unassigned-space">{{element.area || '-'}}</span></td>
                                <td>{{element.unit}}</td>
                                <BoqLineItemRow 
                                :data="element" :ReformatNumber="ReformatNumber" :selectedColumns="selectedColumns" />
                                <td class="sticky-right boq-unassigned-row-actions">
                                    <div v-if="!IsQuotationPublished" class="boq-unassigned-row-actions__inner">
                                        <button class="btn" @click="OpenEditLineItem(element)"
                                        ref="ModalUpdateLineItemBtn"
                                        data-bs-toggle="modal" data-bs-target="#ModalUpdateLineItem"
                                        >
                                            <i class="fa-solid fa-edit"></i>
                                        </button>
                                        <button class="btn boq-unassigned-row-action--delete"
                                        :title="$t('Delete')"
                                        @click="OpenDelete(element._id)" > <i class="fa-solid fa-trash-can"></i> </button>
                                    </div> 
                                </td>
                            </tr>
                        </template>
                    </tbody>
                    <tfoot v-if="LineItemData.length > 0">
                        <tr class="boq-unassigned-table__totals-row">
                            <td v-if="!IsQuotationPublished"></td>
                            <td :colspan="IsQuotationPublished ? 6 : 5" class="boq-unassigned-table__totals-label">
                                {{ $t('Totals') }}
                            </td>
                            <td
                            v-for="column in selectedBoqColumns"
                            :key="column.value"
                            class="text-end boq-unassigned-table__totals-value"
                            >
                                <span v-if="totalColumnGroups.includes(column.group)"
                                :class="{ 'text-danger': unassignedTotals[column.lineItemIndex] < 0 }">
                                    {{ ReformatNumber(unassignedTotals[column.lineItemIndex], column.minDecimal, column.maxDecimal) }}
                                </span>
                            </td>
                            <td class="sticky-right"></td>
                        </tr>
                    </tfoot>
            </table>
        </div>
        </div>
   </div>
   </div>
    <ModalLineItemColumnFilter :UpdateSelectedColumn="ChangeSelectecColumnValue"/>
    <Modal idModal="BoqAttachModal" :headerTitle="$t('Attach to Section')" modalSize="modal-md" >
        <div class="form-group">
            <label>{{ $t('Main Section') }}</label>
            <select class="form-select" v-model="selectedFirstSection" @change="ChangeSection(1)">
                <option value="">{{ $t('Please Select') }}</option>
                <option :value="item._id" v-for="(item,key) in firstSections" class="text-capitalize" :key="key"> {{ item.name }}</option>
            </select>
        </div>
        <div class="form-group" v-if="secondSections.length!=0">
            <label>{{$t('Sub Section')}}</label>
            <select class="form-select" v-model="selectedSecondSection" @change="ChangeSection(2)">
                <option value="">{{ $t('Please Select') }}</option>
                <option :value="item._id" v-for="(item,key) in secondSections" class="text-capitalize" :key="key"> {{ item.name }}</option>
            </select>
        </div>
        <div class="form-group" v-if="thirdSections.length!=0">
            <label>{{ $t('Sub Section') }}</label>
            <select class="form-select" v-model="selectedThirdSection">
                <option value="">{{ $t('Please Select') }}</option>
                <option :value="item._id" v-for="(item,key) in thirdSections" class="text-capitalize" :key="key"> {{ item.name }}</option>
            </select>
        </div>
        <div class="form-group">
            <label>{{ $t('Selected Line Items To be Moved') }}</label>
            <input class="form-control-plaintext text-capitalize" readonly :value="selectedLineItems.length"/>
            <small class="text-warning" v-if="selectedLineItems.length==0">{{ $t('No Selected Line Items') }}</small>
        </div>
        <template v-slot:actions>
            <button type="button" ref="closeBoqAttachModal" :class="isDeluxeLayout ? 'custom-btn custom-btn--cancel' : 'btn btn-outline-danger'" data-bs-dismiss="modal"><i class="fa-solid fa-xmark"></i> {{ $t('Cancel') }}</button>
            <button type="button" :class="isDeluxeLayout ? 'custom-btn' : 'btn btn-primary'" v-if="selectedLineItems.length!=0" @click="AttachToParent"><i class="fa-solid fa-link"></i> {{$t('Attach')}}</button>
        </template>
    </Modal>
    <Modal idModal="BoqDeleteLineItemsModal" :headerTitle="$t('Delete Line Items')" modalSize="modal-md" >
        
        <div class="form-group">
            <label>{{ $t('Line Items Count to be Deleted') }}</label>
            <input class="form-control-plaintext text-capitalize" readonly :value="selectedLineItems.length"/>
            <small class="text-warning" v-if="selectedLineItems.length==0">{{ $t('No Selected Line Items') }}</small>
        </div>
        <template v-slot:actions>
            <button type="button" ref="closeBoqDeleteLineItemsModal" :class="isDeluxeLayout ? 'custom-btn custom-btn--cancel' : 'btn btn-outline-danger'" data-bs-dismiss="modal"><i class="fa-solid fa-xmark"></i> {{ $t('Cancel') }}</button>
            <button type="button" :class="isDeluxeLayout ? 'custom-btn boq-delete-modal__confirm' : 'btn btn-primary'" v-if="selectedLineItems.length!=0" @click="DeleteLineItemsPermanent"><i class="fa-solid fa-trash-can"></i> {{$t('Delete')}}</button>
        </template>
    </Modal>
    <ModalUpdateLineItem
    :selectedLineItem="updateLineItem"
    :boqId="boqId"
    :isHighLevelEstimate="true"
    :UpdateLineItemFunc="UpdateHighLevelBoqLineItem" 
    :boqList="[]" :ResetData="GetData" />
    <BoqItemImagesModal
    v-if="imageItem"
    :boqId="boqId"
    :item="imageItem"
    imageContextType="high-level"
    :allowEdit="true"
    @close="CloseImages"
    @images-changed="UpdateImageSummary" />
</template>


<script>
import Modal  from '@/components/layout/Modal.vue'
import BreadcrumbsProject  from '@/components/projectInformation/BreadcrumbsProject.vue'
import ModalLineItemColumnFilter from '@/components/boq/ModalLineItemColumnFilter.vue'
import ModalUpdateLineItem from '@/components/boq/ModalUpdateLineItem.vue'
import {GetHighLevelProject,GetHighLevelBoq, GetHighLevelBoqLineItemsByBits, AddHleUnassignedToNewBoqCreatorSection,
    DeleteHighLevelBoqLineItem, GetHleNewBoqCreatorSectionsRaw, DeleteHleLineItemsNewBoqCreator, UpdateHighLevelBoqLineItem} from '@/actions/boqActions.js'

import {LinkList,GetDistinctValues, GetDistinctValuesObject, ReformatNumber, ModuleActionList, LineItemRemarks, BoQColumnList } from '@/helpers/utilities.js'
import 'dotenv/config'
import {mapActions,mapGetters} from 'vuex'
import Loader  from '@/components/layout/Loader.vue'
import BoqLineItemRow from '@/components/boq/BoqLineItemRow.vue'
import BoqLineItemRowThead from '@/components/boq/BoqLineItemRowThead.vue'
import FigureCurrencyNote from '@/components/layout/FigureCurrencyNote.vue'
import BoqItemImage from '@/components/boq/BoqItemImage.vue'
import BoqItemImagesModal from '@/components/boq/BoqItemImagesModal.vue'
import {GetBoqImageSummaries} from '@/actions/boqImageActions.js'
export default {
    name : 'HleBoqBuilderUnassigneds',
    components : {
        BreadcrumbsProject, Loader,
        ModalLineItemColumnFilter,ModalUpdateLineItem,
        BoqLineItemRow,BoqLineItemRowThead, Modal,FigureCurrencyNote,BoqItemImage,BoqItemImagesModal
    },
    mounted : async function(){
        const self = this

        self.ChangeActiveLink(LinkList.HIGH_LEVEL_ESTIMATES)
        const boqItem = await GetHighLevelBoq(self.boqId)
        self.boqItem = boqItem
        
        self.boqUrlname = boqItem.name  + ' - (' + boqItem.currencyId.sign +') '  + boqItem.currencyId.shortName  +' '+ boqItem.currencyId.name
        self.hleProject = await GetHighLevelProject(self.hleProjectId)
        await self.GetData(self.boqListParams)
        const lineItemCols =  window.localStorage.getItem('lineItemColumns')
        if(lineItemCols){
            self.selectedColumns = lineItemCols.split(',').map(Number)
        }
        const rawSections = await GetHleNewBoqCreatorSectionsRaw(boqItem._id)
        self.rawSections = rawSections.sections
        
        self.firstSections = rawSections.sections.filter(u=>u.level==1)
        self.searchableAreas = GetDistinctValues(self.lineItems.map(u=>u.area).filter(value => value !== null && value !== undefined && value !== ''))
        self.searchableAreas.push(self.$i18n.t('NO SPACE ID'))
        const getLineItemCostCode = 
        self.userInfo.language =='en' ? 
            self.lineItems.map(u=>{
                return {
                    _id : u.costCodeId,
                    costName : u.costCode + ' ' +u.name
                }
            }) 
        :
            self.lineItems.map(u=>{
                return {
                    _id : u.costCodeId,
                    costName : u.costCode + ' ' +u.nameCh
                }
            }) 
        self.searchableCostCodes = GetDistinctValuesObject(getLineItemCostCode)

    },
    data(){
        return {
            boqUrlname : '',
            hleProject : {},
            hleProjectId : this.$route.params.id ,
            ModuleActionList,
            totalColumnGroups : [3, 6, 7],
            selectedLineItems : [],
            lineItems : [],
            LineItemData : [],
            rawSections : [],
            firstSections : [],
            secondSections : [],
            thirdSections : [],
            selectedFirstSection : '',
            selectedSecondSection : '',
            selectedThirdSection : '',
            boqId : this.$route.params.boqId,
            section : {},
            boqItem : {
                status : 1,
                currencyId  : {
                    sign : '',
                }
            },
            projectUrl : process.env.VUE_APP_PROJECT_URL,
            selectedArea : [],
            selectedCostCode : [],
            dataReady : false,
            OpenFilter : false,
            selectedColumns : [],
            boqListParams : {
                page : 1,
                id :  this.$route.params.boqId,
                costCode : '',
                area : '',
                skip : 0,
                take : 50
            },
            updateLineItem : {
                unit : '',
                parents : [
                    {
                        name : ''
                    },
                    {
                        name : ''
                    }
                ]
            },
            isOnProcess : false,
            isRunning : false,
            imageItem : null,
            imageTrigger : null,
            imageSummaries : {},
            imageSummaryStates : {},
            loadedImagePreviews : {},
            LineItemRemarks
        }
    },
    methods : {
        ...mapActions(['ChangeActiveLink']),
        ReformatNumber,
        UpdateHighLevelBoqLineItem,
        ChangeSection(level){
            const self = this
            if(level==1){
                const selected = self.selectedFirstSection

                self.secondSections = self.rawSections.filter(u=>u.parentId==selected)
                self.thirdSections = []

                self.selectedSecondSection = ''
                self.selectedThirdSection = ''
            }else if(level==2){
                const selected = self.selectedSecondSection

                self.thirdSections = self.rawSections.filter(u=>u.parentId==selected)

                self.selectedThirdSection = ''
            }
        },
        ClearField(type){
            if(type==1){
                this.selectedArea = []
            }
            else{
                this.selectedCostCode = []
            }
        },
        async GetData(){
            const self = this
            self.lineItems = []
            let willFetch = true
            self.boqListParams.skip = 0
            self.dataReady = false
            while(willFetch){
                const bits = await GetHighLevelBoqLineItemsByBits(self.boqListParams)
                if(bits.length!=0){
                    self.lineItems.push(...bits)
                }
                if(bits.length==self.boqListParams.take){
                    self.boqListParams.skip += 50
                }else{
                    willFetch = false
                }
            }
            self.ReformatRows()
            await self.LoadImageSummaries()
            self.dataReady = true
        },
        async LoadImageSummaries(){
            const itemIds = [...new Set(this.lineItems.map(item => item._id).filter(Boolean))]
            this.imageSummaries = {}
            this.imageSummaryStates = Object.fromEntries(itemIds.map(id => [id, 'loading']))
            this.loadedImagePreviews = {}

            for(let offset = 0; offset < itemIds.length; offset += 100){
                const batch = itemIds.slice(offset, offset + 100)
                try{
                    const result = await GetBoqImageSummaries(this.boqId, batch, 'high-level')
                    batch.forEach(id => {
                        this.imageSummaries[id] = result.items?.[id] || { image: null, count: 0 }
                        this.imageSummaryStates[id] = 'ready'
                    })
                }catch{
                    batch.forEach(id => { this.imageSummaryStates[id] = 'error' })
                }
            }
        },
        OpenImages(item, event){
            this.imageTrigger = event.currentTarget
            this.imageItem = item
        },
        CloseImages(){
            this.imageItem = null
            this.$nextTick(() => this.imageTrigger?.focus())
        },
        UpdateImageSummary({itemId, image, count}){
            this.imageSummaries[itemId] = {image, count}
            this.imageSummaryStates[itemId] = 'ready'
            this.loadedImagePreviews[itemId] = false
        },
        ReformatRows(){
            const self = this 
            if(self.lineItems.length==0){
                return []
            }

            self.LineItemData = []
      
            let lineItemSubCount = 1
      
            self.lineItems.map((u)=>{
                const supplyAndInstallCombine = u.supplyAndInstall ? u.supplyAndInstall : (u.costRate || 0) + (u.installRate || 0)
                const  supplyAndInstallTotal = supplyAndInstallCombine * u.quantity


                u.label = lineItemSubCount++
                u.bufferCostTotal = u.costTotal - u.costTotalRaw
                u.bufferSellRate = u.sellRate - u.sellRateRaw
                u.bufferSellTotal = u.sellTotal - u.sellTotalRaw
                u.bufferGp = u.gp - u.gpRaw
                u.supplyAndInstallTotal = supplyAndInstallTotal
             
                self.LineItemData.push(u)
                return u
            })
        },
        ChangeSelectecColumnValue(value){
            this.selectedColumns = value
        },
        OnChecked(e){
            const isChecked = e.target.checked

            this.selectedLineItems  = []
            if(isChecked){
                this.selectedLineItems.push(...this.lineItems.map(u=>u._id))
            }
            
        },
        async AttachToParent(){
            const self  = this
            if(self.isOnProcess){
                return
            }  

            const swalTitle = self.$i18n.t('Attach to Section')
            if(self.selectedLineItems.length==0){
                self.$swal(swalTitle, self.$i18n.t('Select at least one line item before attaching it to a section.'), 'warning')
                return
            }

            if(!self.selectedFirstSection){
                self.$swal(swalTitle, self.$i18n.t('Select a main section before attaching the selected line items.'), 'warning')
                return
            }

            if(self.secondSections.length!=0 && !self.selectedSecondSection){
                self.$swal(swalTitle, self.$i18n.t('The selected main section contains sub sections. Select a sub section before attaching the line items.'), 'warning')
                return
            }

            if(self.thirdSections.length!=0 && !self.selectedThirdSection){
                self.$swal(swalTitle, self.$i18n.t('The selected sub section contains another level. Select the final sub section before attaching the line items.'), 'warning')
                return
            }

            const selected = self.selectedThirdSection || self.selectedSecondSection || self.selectedFirstSection

            const result = await AddHleUnassignedToNewBoqCreatorSection({
                boqId : self.boqId,
                lineItems : self.selectedLineItems,
                parentId : selected
            })

            if(result!='OK'){
                const message = String(result || '').toLowerCase().includes('missing parameter')
                    ? self.$i18n.t('The attachment request is incomplete. Select every required section level and at least one line item, then try again.')
                    : result
                self.$swal(swalTitle, message, 'warning')
                return
            }
            self.selectedLineItems = []

            self.$swal(swalTitle, self.$i18n.t('Updated Successfully'), 'success')
            self.$refs.closeBoqAttachModal.click()
            await self.GetData()
        },
        async DeleteLineItemsPermanent(){
            const self  = this
            if(self.isOnProcess){
                return
            }  

            const swalTitle = self.$i18n.t('Delete Line Items')

            if(self.selectedLineItems.length==0){
                self.$swal(swalTitle, self.$i18n.t('No line item selected'), 'warning')
                return
            }

            const deletePayload = {
                boqId : self.boqId,
                lineItems : self.selectedLineItems,
            }
            const result = await DeleteHleLineItemsNewBoqCreator(deletePayload)

            if(result!='OK'){
                self.$swal(swalTitle, result, 'warning')
                return
            }

            self.selectedLineItems = []
            self.$swal(swalTitle, self.$i18n.t('Deleted Successfully'), 'success')
            self.$refs.closeBoqDeleteLineItemsModal.click()
            await self.GetData()
        },
        OpenEditLineItem(data){
            this.updateLineItem = JSON.parse(JSON.stringify(data))
            console.log(data)
            this.updateLineItem.id = data._id
            this.updateLineItem.markUp = this.updateLineItem.markUp * 100
        },
        OpenDelete(id){
            const self = this
            const swalTitle = self.$i18n.t('Delete')
            self.$swal({
                title: swalTitle,
                text : self.$i18n.t('Bill of Quantity Module.Remove Item from Bill of Quantities') + '?',
                showCancelButton: true,
                icon: 'warning',
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !self.$swal.isLoading(),
                preConfirm: () => DeleteHighLevelBoqLineItem(id)
            }).then((res)=>{
                if(res.isConfirmed){
                    if(res.value=='OK'){
                        const getIndex = self.lineItems.findIndex(u=>u._id==id)
                        if(getIndex!=-1){
                            self.lineItems.splice(getIndex,1)
                            self.selectedLineItems = self.selectedLineItems.filter(itemId => itemId !== id)
                            self.ReformatRows()
                        }
                        self.$swal(swalTitle, self.$i18n.t('Deleted Successfully'), 'success')
                    }else{
                        self.$swal(swalTitle,res.value,'warning')
                    }
                }
            })
        },
    },
    computed : {
        ...mapGetters(['userInfo']),
        isDeluxeLayout(){
            return this.$route.meta.layout === 'deluxe'
        },
        activeFilterCount(){
            return this.selectedArea.length + this.selectedCostCode.length
        },
        selectionProgress(){
            if(this.lineItems.length === 0){
                return 0
            }

            return Math.round((this.selectedLineItems.length / this.lineItems.length) * 100)
        },
        allItemsSelected(){
            return this.lineItems.length > 0 && this.selectedLineItems.length === this.lineItems.length
        },
        selectedBoqColumns(){
            return BoQColumnList.filter(column => this.selectedColumns.includes(column.value))
        },
        unassignedTotals(){
            return this.LineItemData.reduce((totals, lineItem) => {
                this.selectedBoqColumns.forEach(column => {
                    if(this.totalColumnGroups.includes(column.group)){
                        totals[column.lineItemIndex] = (totals[column.lineItemIndex] || 0) + (Number(lineItem[column.lineItemIndex]) || 0)
                    }
                })

                return totals
            }, {})
        },
        IsQuotationPublished(){
            return false
        }
    }
}
</script>

<style scoped>
.boq-unassigned-table__totals-row td {
    background: #f2eeeb;
    border-top: 2px solid #d8c6b9;
    bottom: 0;
    box-shadow: 0 -0.35rem 0.65rem -0.65rem rgba(47, 41, 37, 0.55);
    color: #2f2925;
    font-weight: 700;
    padding-bottom: 0.85rem;
    padding-top: 0.85rem;
    position: sticky;
    vertical-align: middle;
    z-index: 70;
}

.boq-unassigned-table__totals-label {
    text-align: right;
}

.boq-unassigned-table__totals-value {
    white-space: nowrap;
}

.boq-unassigned-table__image,
.boq-unassigned-image-cell {
    min-width: 9rem;
    width: 9rem;
    text-align: center;
}

.boq-unassigned-image-button {
    display: inline-flex;
    min-width: 7.5rem;
    min-height: 5.5rem;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.35rem;
    overflow: hidden;
    border: 1px solid #e4d8cf;
    border-radius: 0;
    padding: 0;
    background: #fffaf7;
    color: #a64200;
}

.boq-unassigned-image-button:hover,
.boq-unassigned-image-button:focus {
    border-color: #a64200;
    background: #fff3eb;
}

.boq-unassigned-image-button--empty {
    min-height: auto;
    border-color: transparent;
    background: transparent;
}

.boq-unassigned-image-button--empty:hover,
.boq-unassigned-image-button--empty:focus {
    border-color: transparent;
    background: transparent;
    color: #7f330b;
}

.boq-unassigned-image-frame,
.boq-unassigned-image-preview {
    display: block;
    width: 7.5rem;
    height: 4.75rem;
}

.boq-unassigned-image-preview {
    object-fit: cover;
    object-position: top;
}

.boq-unassigned-image-preview--loading {
    opacity: 0;
}

.boq-unassigned-image-button small {
    padding: 0 0.35rem 0.3rem;
    font-size: 0.65rem;
    font-weight: 700;
}
</style>
